/**
 * DR_META — rattachement plan↔observation.
 *
 * Le champ `observationId` du DR_META (cf. `xdata.ts`) porte le `localId` (UUID)
 * de la FieldObservation à laquelle l'entité est rattachée. Le PLAN est la source
 * de vérité de l'appartenance : la DB ne stocke pas la liste des entités (une
 * liste d'uids en base divergerait au premier clone/merge offline, le XData suit
 * l'entité partout et survit au round-trip DWG).
 *
 * Une entité de fond de plan (sans DR_META) peut être rattachée : elle reçoit une
 * identité minimale stampée à la volée (`background: true`), même pattern que
 * l'oplog.
 *
 * Best-effort : une entité illisible n'interrompt jamais l'opération globale.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { openAs, getEntityHandle, findEntityByHandle } from "../core/oda";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { getModelSpaceId } from "../parsing/viewports";
import { UNDO_LAYER_NAME } from "../database/layers";
import { readEntityMeta, writeEntityMeta } from "./xdata";
import { getEntityType } from "../entities/detection";
import { getPhotoMarkerAttributeValue } from "../photomarker/transform";
import { readMarkerPhotoIds } from "../photomarker/photo-link";
import { readKindAndText } from "../parsing/activity";
import { measureEntity, type EntityOrientation } from "./measure";
import { readEntityBbox } from "./extents";
import type { WorldBbox } from "@/utils/plan-analysis/types";
import { v4 as uuid } from "uuid";

/**
 * Rattache (ou détache avec `null`) les entités `handles` à l'observation donnée.
 * Une entité sans DR_META reçoit une identité minimale à la volée (fond de plan).
 * @returns le nombre d'entités effectivement stampées.
 */
export function setEntitiesObservation(
  viewer: DRDrawingViewer,
  handles: string[],
  observationId: string | null,
): number {
  const module = viewer.module;
  const db = viewer.database;
  if (!db || handles.length === 0) return 0;

  let stamped = 0;
  for (const h of handles) {
    const id = findEntityByHandle(module, db, h);
    if (!id) continue;
    try {
      const meta = readEntityMeta(module, id);
      if (meta) {
        if (meta.observationId !== observationId) {
          writeEntityMeta(module, id, { ...meta, observationId });
        }
        stamped++;
      } else if (observationId !== null) {
        // Fond de plan jamais stampé — identité minimale à la volée.
        writeEntityMeta(module, id, {
          entityUid: uuid(),
          kind: getEntityType(module, id),
          markerNumber: null,
          createdAt: null,
          defectName: null,
          background: true,
          observationId,
        });
        stamped++;
      }
    } catch {
      // best-effort — une entité illisible n'interrompt pas le rattachement
    }
    deleteObj(id);
  }
  return stamped;
}

/** Entité rattachée à une observation, telle que vue sur le plan. */
export interface ObservationEntityRef {
  handle: string;
  /** Identité stable DR_META (clé de corrélation, clone-safe). */
  entityUid: string;
  /** `localId` de la FieldObservation rattachée. */
  observationId: string;
  /** Nature CAD de l'entité (EntityType du DR_META). */
  kind: string;
  /** Nom du note_config (désordre de la légende) actif au dessin, si connu. */
  defectName: string | null;
  /** Calque de l'entité. */
  layer: string;
  /** Longueur développée (unités du plan), null si non mesurable. */
  length: number | null;
  /** Surface (contour fermé, unités²), null sinon. */
  area: number | null;
  /** Orientation dominante (tracé linéaire ouvert uniquement), null sinon. */
  orientation: EntityOrientation | null;
  /** Contenu textuel (texte, MText, MLeader) — les mots de l'inspecteur. */
  text: string | null;
  /**
   * Numéro du marqueur photo (entités marqueur uniquement, `null` sinon ou si
   * l'attribut affiché n'est pas numérique — légende en mode date). Ne sert à
   * rapprocher une photo que pour départager plusieurs marqueurs d'une même
   * observation (cf. `matchObservationPhotos`).
   */
  markerNumber: number | null;
  /**
   * Libellé brut du marqueur, tel qu'il est écrit sur le plan (« 12 »,
   * « 16:47:48 »…). `null` si l'entité n'est pas un marqueur photo.
   */
  markerLabel: string | null;
  /** `DiagnosticImage.id` portés par le marqueur (cf. `photo-link.ts`). */
  photoIds: number[];
  /**
   * Emprise monde de l'entité, null si illisible — sert au cadre de son
   * observation sur le plan.
   */
  bbox: WorldBbox | null;
}

/**
 * Numéro du marqueur : seulement si le libellé EST un nombre.
 *
 * L'ancienne règle prenait la première suite de chiffres, comme le backend le
 * fait sur un nom de fichier. Sur un marqueur horodaté (« 16:47:48 ») elle
 * renvoyait 16 — un numéro inventé, qui se rattachait silencieusement à la
 * mauvaise photo. Mieux vaut aucun numéro : l'UI signale alors le marqueur.
 */
function extractNumber(text: string | null): number | null {
  if (!text) return null;
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? parseInt(trimmed, 10) : null;
}

/**
 * Liste les entités rattachées à une observation, groupées par `observationId`.
 * UNE seule traversée de l'espace objet ; les entités masquées (UNDO_LAYER) sont
 * exclues — une entité supprimée ne compte plus dans l'observation.
 */
export function listObservationEntities(
  viewer: DRDrawingViewer,
): Map<string, ObservationEntityRef[]> {
  const module = viewer.module;
  const out = new Map<string, ObservationEntityRef[]>();
  const modelSpaceId = getModelSpaceId(viewer);
  if (!modelSpaceId) return out;

  const rec = openAs(
    modelSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForRead,
  );
  const iter = rec.newIterator(true, true, false);
  iter.start(true, true);
  while (!iter.done()) {
    const id = iter.objectId();
    try {
      const meta = readEntityMeta(module, id);
      if (meta?.observationId) {
        const ent = iter.entity(OpenMode.kForRead, false);
        try {
          const layerStr = ent.layer();
          const layer = layerStr.toString();
          deleteObj(layerStr);

          if (layer !== UNDO_LAYER_NAME) {
            const measure = measureEntity(module, ent);
            const markerLabel = getPhotoMarkerAttributeValue(module, id);
            const ref: ObservationEntityRef = {
              handle: getEntityHandle(id),
              entityUid: meta.entityUid,
              observationId: meta.observationId,
              kind: meta.kind,
              defectName: meta.defectName,
              layer,
              length: measure.length,
              area: measure.area,
              orientation: measure.orientation,
              text: readKindAndText(module, id, ent).text ?? null,
              // null pour toute entité non-marqueur (guard interne).
              markerNumber: extractNumber(markerLabel),
              markerLabel,
              photoIds:
                markerLabel === null ? [] : readMarkerPhotoIds(module, id),
              bbox: readEntityBbox(module, ent),
            };
            const list = out.get(meta.observationId);
            if (list) list.push(ref);
            else out.set(meta.observationId, [ref]);
          }
        } finally {
          deleteObj(ent);
        }
      }
    } catch {
      // entité illisible — on ignore
    }
    iter.step(true, true);
  }
  deleteObj(iter);
  deleteObj(rec);

  return out;
}

/**
 * Détache TOUTES les entités du plan rattachées à `observationId` (suppression
 * d'une observation). Traversée complète — on ne peut pas se fier à une liste de
 * handles côté appelant (clones, merges).
 * @returns le nombre d'entités détachées.
 */
export function clearObservationEntities(
  viewer: DRDrawingViewer,
  observationId: string,
): number {
  const module = viewer.module;
  const modelSpaceId = getModelSpaceId(viewer);
  if (!modelSpaceId) return 0;

  let cleared = 0;
  const rec = openAs(
    modelSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForRead,
  );
  const iter = rec.newIterator(true, true, false);
  iter.start(true, true);
  while (!iter.done()) {
    const id: DrawingModule.OdDbObjectId = iter.objectId();
    try {
      const meta = readEntityMeta(module, id);
      if (meta?.observationId === observationId) {
        writeEntityMeta(module, id, { ...meta, observationId: null });
        cleared++;
      }
    } catch {
      // entité illisible — on ignore
    }
    iter.step(true, true);
  }
  deleteObj(iter);
  deleteObj(rec);

  return cleared;
}
